<script setup lang="ts">
import { computed } from 'vue'
import { toFixedPrice } from '@/composables/commonFunctions'

const props = defineProps<{
  scheme: any
  action: string
  tenant: string
  productName: string
  isMonthly?: boolean
  productId?: string
}>()

const productSlug = computed(() => (props.productName ?? '').toLowerCase().replace(/\s+/g, ''))

const selectedAddons = computed(() => {
  const single = props.scheme?.Addons?.filter((x: any) => x.IsSelected && !x.IsIncluded) ?? []

  const grouped =
    props.scheme?.GroupedAddons
      ?.flatMap((group: any) => group.GroupAddons ?? [])
      ?.filter((x: any) => x.IsSelected && !x.IsIncluded) ?? []

  return [...single, ...grouped]
})

const hasSelectedAddons = computed(() => selectedAddons.value.length > 0)

// Headline figure: monthly accounts show the total cost of paying monthly
// (annual premium + credit), everyone else shows the annual total.
const headlinePayable = computed(() =>
  props.isMonthly
    ? props.scheme?.PaymentPlan?.TotalPayable ?? props.scheme?.PaymentPlan?.BaseTransactionAmount
    : props.scheme?.PaymentPlan?.BaseTransactionAmount
)

import { useQuoteProductConfig, resolveSchemeImageStyles } from '@/components/Quote/composables/useQuoteProductConfig'
const { resolveSchemeImage } = useQuoteProductConfig(computed(() => props.productId))
const schemeImage = computed(() => resolveSchemeImage(props.scheme))
const schemeImageStyles = computed(() => resolveSchemeImageStyles(schemeImage.value))
</script>

<template>
  <div class="card-shadow card rounded-lg mb-4 overflow-hidden">
    <div class="card-body p-0">
      <div class="d-flex justify-content-between align-items-center border-bottom px-3">
          <div class="d-flex align-items-center">
            <em class="far fa-shield-alt mr-2 text-info fs-24"></em>
            <span class="small text-uppercase text-muted font-weight-bold">{{ scheme?.Insurer.Description }}</span>
          </div>
          <div
              class="d-flex align-items-center justify-content-center mr-3"
            >
                <img
                    v-if="schemeImage"
                    loading="lazy"
                    class="img-fluid"
                    :src="schemeImage.image"
                    :style="schemeImageStyles.image"
                    :alt="scheme?.Insurer.Description"
                />
          </div>
      </div>

      <div class="row no-gutters">
        <div class="col-12 col-xl-9 border-right">
          <div class="row no-gutters border-bottom">
            <div class="col-12 col-md-3 p-20 border-right d-flex flex-column">
              <div class="text-muted fs-12 mb-1">
                Insurance Premium <br />
                <span class="small">(Including IPT)</span>
              </div>
              <div class="font-weight-bold text-dark mt-auto">
                {{ toFixedPrice(scheme.QuotedPremium?.TotalPayable) }}
              </div>
            </div>

            <div class="col-12 col-md-3 p-20 border-right d-flex flex-column">
              <div class="text-muted fs-12 mb-1">
                Insurance Premium Tax (IPT)
              </div>
              <div class="font-weight-bold text-dark mt-auto">
                {{ toFixedPrice(scheme.QuotedPremium?.Ipt) }}
              </div>
            </div>

            <div class="col-12 col-md-3 p-20 border-right d-flex flex-column">
              <div class="text-muted fs-12 mb-1">CETA Fee <br/> 
                <span class="small" v-if="hasSelectedAddons">(Including Addons Fee)</span>
              </div>
              <div class="font-weight-bold text-dark mt-auto">
                {{ toFixedPrice(scheme.QuotedPremium?.AdminFee) }}
              </div>
            </div>

            <div class="col-12 col-md-3 p-20 d-flex flex-column">
              <div class="text-muted fs-12 mb-1">Broker Fee</div>
              <div class="font-weight-bold text-dark mt-auto">
                {{ toFixedPrice(scheme?.AgentAdminFee ?? 0) }}
              </div>
            </div>
          </div>

          <div v-if="hasSelectedAddons">
            <div class="bgc-teal-50 p-20">
              <div
                v-for="(addon, index) in selectedAddons"
                :key="addon.Code"
                class="row py-2"
                :class="index < selectedAddons.length - 1 ? 'border-bottom' : ''"
              >
                <div class="col-12 col-md-3">
                  <div class="text-muted fs-12">Addon Name</div>
                  <strong class="text-dark">{{ addon.Name }}</strong>
                </div>

                <div class="col-12 col-md-3">
                  <div class="text-muted fs-12">Addon IPT amount</div>
                  <strong class="text-dark">{{ toFixedPrice(addon.Ipt ?? 0) }}</strong>
                </div>

                <div class="col-12 col-md-3">
                  <div class="text-muted fs-12">Addon Price</div>
                  <strong class="text-dark">{{ toFixedPrice(addon.TotalPayable) }}</strong>
                </div>

                <!-- Leaving blank column for layout purposes to align with content above -->
                <div class="col-12 col-md-3"></div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-12 col-xl-3 d-flex align-items-center">
          <div class="w-100 px-5 py-4">
            <div class="border-bottom mb-4 pb-4">
              <span class="h4 text-dark mr-1">£</span>
              <span class="display-4 font-weight-bold text-dark" style="font-size: 2.3rem;">
                {{ toFixedPrice(headlinePayable).replace('£', '') }}
              </span>

              <div class="small text-muted">
                {{ isMonthly ? 'Total cost paying monthly' : 'Total annual cost' }} <br />
                <span class="small text-muted">(Including IPT and all applicable fees)</span>
              </div>
            </div>

            <div>
              <span class="small text-muted">Quote Reference:</span><br />
              <strong class="font-weight-bold text-dark">{{ scheme?.QuoteReference }}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>